/**
 * Technical Module Schemas
 * Kỹ thuật: Tiêu chuẩn SP, Bình bản, Bản In, Khuôn bế, Lưới, Khuôn ép nhũ
 */

import { z } from 'zod';
import { optionalNumber } from './numeric-field.schema';

const optStr = (max = 500) => z.preprocess(
    (v) => (v === null || v === undefined ? undefined : v),
    z.string().max(max).optional().or(z.literal(''))
);

export interface TechnicalSpec {
    tsktId: string;
    productId: string;
    productName: string;
    customerId: string;
    customerName: string;
    status: string;
    createdDate: string;
    createdBy: string;
    material?: {
        paperType: string;
        grammage: number;
    };
    finishedSize?: {
        lengthMm: number;
        widthMm: number;
        heightMm: number;
    };
}

export interface Plate {
    plateId: string;
    productId: string;
    productName: string;
    customerId: string;
    colors: number;
    colorNames: string[];
    size: string;
    location: string;
    usesCount: number;
    status: string;
    plateType?: string;
    notes?: string;
    createdAt?: string;
    // CTP Prepress Proofing, Mounting & Remake (Phase 222)
    proofStatus?: string;
    proofApprovedAt?: string | Date;
    proofApprover?: string;
    proofNotes?: string;
    proofSampleUrl?: string;
    activeMachineId?: string;
    activeMachineName?: string;
    mountedAt?: string | Date;
    mountedBy?: string;
    damageReason?: string;
    damagedColors?: string[];
    remakeCount?: number;
}

export interface Layout {
    layoutId: string;
    name: string;
    productId: string;
    sheetSizeX: number;
    sheetSizeY: number;
    wasteMarginX: number;
    wasteMarginY: number;
    upsCount: number;
    machineCompat: string[];
    status: string;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface DieCut {
    dieId: string;
    name: string;
    customerId: string;
    dimensions: string;
    ups: number;
    knifeLengthM: number;
    machineCompat: string[];
    location: string;
    status: string;
    condition?: number;
}

export const ctpPlateLogFormSchema = z.object({
  logDate: z.string().min(1, { message: 'Ngày không được để trống' }),
  productId: z.string().optional().nullable(),
  productName: z.string().min(1, { message: 'Tên sản phẩm không được để trống' }).max(200, { message: 'Tên quá dài' }),
  poReference: z.string().optional().nullable(),
  importQty: z.number().int().min(0, { message: 'Số lượng nhập phải >= 0' }),
  usedQty: z.number().int().min(0, { message: 'Số lượng sử dụng phải >= 0' }),
  stockQty: z.number().int().min(0, { message: 'Tồn kho phải >= 0' }),
  plateType: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type CtpPlateLogFormData = z.infer<typeof ctpPlateLogFormSchema>;

export interface CtpPlateLog {
  id: string;
  logDate: string;
  productId?: string | null;
  productName: string;
  product?: { id: string; productCode: string; productName: string } | null;
  poReference?: string | null;
  importQty: number;
  usedQty: number;
  stockQty: number;
  plateType?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const technicalSpecFormSchema = z.object({
    customerId: optStr(),
    productId: z.string().min(1, 'Sản phẩm là bắt buộc'),
    productName: z.string().min(1, 'Tên sản phẩm là bắt buộc'),
    status: optStr().default('PENDING'),
    version: optStr(),
    notes: optStr(),
    materialId: optStr(),
    packingSpec: optStr(),
    process: optStr(),
    layoutId: optStr(),
    dieCutId: optStr(),
    foilDieId: optStr(),
    screenId: optStr(),
    designFile: optStr(),
    designStatus: optStr(),
    designNotes: optStr(),
    specDetails: z.object({
        printSize: optStr(),
        ups: optStr(),
        grainDirection: optStr(),
        finishedSizeLength: optStr(),
        finishedSizeWidth: optStr(),
        finishedSizeHeight: optStr(),
        steps: z.record(z.any()).optional()
    }).optional()
});

export type TechnicalSpecFormData = z.infer<typeof technicalSpecFormSchema>;

export const moldFormSchema = z.object({
    name: z.string().trim().min(1, 'Tên khuôn là bắt buộc'),
    productName: optStr(),
    productId: optStr(),
    customerId: optStr(),
    size: optStr(),
    type: optStr(),
    location: optStr(),
    storageZone: optStr(),
    storageRack: optStr(),
    storageShelf: optStr(),
    storageBin: optStr(),
    machine: optStr(),
    image: optStr(),
    images: z.union([z.array(z.string()), z.string()]).optional().nullable(),
    notes: optStr(),
    status: optStr().default('AVAILABLE'),
    usageCount: optionalNumber('Số lần sử dụng', { int: true }),
    maxImpressions: optionalNumber('Định mức dập tối đa', { int: true, exclusiveMin: true }),
    maintenanceInterval: optionalNumber('Chu kỳ bảo dưỡng', { int: true, exclusiveMin: true }),
    version: z.number().int().optional().nullable().default(1),
    expectedDate: optStr(),
    dieLength: optionalNumber('Dài khuôn', { exclusiveMin: true }),
    dieWidth: optionalNumber('Rộng khuôn', { exclusiveMin: true }),
    productLength: optionalNumber('Dài sản phẩm', { exclusiveMin: true }),
    productWidth: optionalNumber('Rộng sản phẩm', { exclusiveMin: true }),
    ups: optionalNumber('Số UPS', { int: true, min: 1 }),
    isShared: z.boolean().optional().nullable().default(false),
    vendorId: optStr(),
    outsourceCost: optionalNumber('Chi phí làm khuôn'),
});

export type MoldFormData = z.infer<typeof moldFormSchema>;

export const plateFormSchema = z.object({
    name: z.string().min(1, 'Tên bản in là bắt buộc'),
    productName: optStr(),
    productId: optStr(),
    customerId: optStr(),
    size: optStr(),
    colors: z.array(z.string()).optional().nullable(),
    designFileUrl: optStr(),
    ctpFileUrl: optStr(),
    location: optStr(),
    storageZone: optStr(),
    storageRack: optStr(),
    storageShelf: optStr(),
    status: optStr().default('ACTIVE'),
    usageCount: z.preprocess((v) => v === null ? undefined : v, z.union([z.string(), z.number()]).optional()).default(0),
    maxImpressions: z.preprocess((v) => v === null ? undefined : v, z.union([z.string(), z.number()]).optional()).default(40000),
    version: z.number().int().optional().nullable().default(1),
    expectedDate: optStr(),
    vendorId: optStr(),
    outsourceCost: z.preprocess((v) => v === null ? undefined : v, z.union([z.string(), z.number()]).optional()),
});

export type PlateFormData = z.infer<typeof plateFormSchema>;
